import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { AppHeader } from './AppHeader';
import { useResponsive } from '../hooks/useResponsive';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useDiscoverMatches } from '../hooks/useDiscoverMatches';
import { apiRequest } from '../utils/apiUtils';
import ApplyToPublicMatchModal from './ApplyToPublicMatchModal';
import { theme, alpha } from '../theme';

type GenderFilter = '' | 'MALE' | 'FEMALE' | 'MIXED' | 'ANY';

const DiscoverMatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const flags = useFeatureFlags() as Record<string, any>;
  const publicMatchDiscover = Boolean(flags.PUBLIC_MATCH_DISCOVER);
  const flagsLoading = Boolean(flags.loading);

  const [zone, setZone] = useState('');
  const [date, setDate] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [gender, setGender] = useState<GenderFilter>('');
  const [skill, setSkill] = useState('');
  const [applyMatchId, setApplyMatchId] = useState<string | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState('15');
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const { matches, loading, error, searched, search } = useDiscoverMatches();

  const useMyLocation = () => {
    if (locating) return;
    if (!navigator.geolocation) {
      setGeoError('Tu navegador no soporta geolocalización.');
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => {
        setGeoError('No pudimos obtener tu ubicación. Revisá los permisos del navegador.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const canSearch = zone.trim().length >= 2 || !!coords;

  const [zoneAlertOn, setZoneAlertOn] = useState(false);
  const [zoneAlertBusy, setZoneAlertBusy] = useState(false);

  const subscribeZoneAlert = async () => {
    if (!coords || zoneAlertBusy) return;
    setZoneAlertBusy(true);
    try {
      const res = await apiRequest('/matches/public/my-zone-alert', {
        method: 'PUT',
        body: JSON.stringify({ latitude: String(coords.lat), longitude: String(coords.lng), radiusKm }),
      });
      if (!(res as any)?.error) setZoneAlertOn(true);
    } finally {
      setZoneAlertBusy(false);
    }
  };

  const SearchIcon = FaSearch as React.ComponentType<{ size?: number; style?: React.CSSProperties }>;

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void search({
      zone: zone.trim(),
      date,
      timeFrom,
      timeTo,
      matchGenderType: gender || undefined,
      skillLevel: skill || undefined,
      ...(coords
        ? {
            latitude: String(coords.lat),
            longitude: String(coords.lng),
            radiusKm,
            // Si buscás por ubicación sin escribir zona, no filtres por texto.
            ...(zone.trim().length < 2 ? { omitZoneFilter: '1' } : {}),
          }
        : {}),
    });
  };

  if (!flagsLoading && !publicMatchDiscover) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: theme.colors.background,
          padding: isMobile ? '80px 16px 20px' : '20px 40px',
        }}
      >
        <AppHeader title="Descubrir partidos" showBackButton />
        <div
          style={{
            maxWidth: 480,
            margin: '40px auto',
            padding: 32,
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: 16,
            textAlign: 'center',
          }}
        >
          <h2 style={{ color: theme.colors.text, margin: 0, fontFamily: theme.fonts.heading }}>
            Próximamente
          </h2>
          <p style={{ color: theme.colors.textSecondary, marginTop: 8 }}>
            La búsqueda de partidos públicos todavía no está disponible.
          </p>
        </div>
      </div>
    );
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 700,
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
    display: 'block',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    background: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 10,
    color: theme.colors.text,
    fontSize: 14,
    fontFamily: theme.fonts.body,
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.background,
        padding: isMobile ? '80px 16px 20px' : '20px 40px',
      }}
    >
      <AppHeader title="Descubrir partidos" showBackButton />

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div style={{ marginBottom: 16 }}>
          <p style={{ color: theme.colors.textSecondary, fontSize: 14, margin: 0 }}>
            Buscá partidos públicos en tu zona y postúlate al organizador.
          </p>
        </div>

        {/* Filters */}
        <form
          onSubmit={onSearch}
          style={{
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: 16,
            padding: 20,
            marginBottom: 24,
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <div style={{ gridColumn: isMobile ? 'auto' : 'span 4' }}>
            <label htmlFor="discover-zone" style={labelStyle}>Zona / barrio</label>
            <input
              id="discover-zone"
              type="text"
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              placeholder="Ej: Palermo, Belgrano, La Plata..."
              style={inputStyle}
              autoComplete="off"
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={useMyLocation}
                disabled={locating}
                style={{
                  padding: '8px 12px',
                  background: coords ? alpha('primary', '20') : 'transparent',
                  color: theme.colors.primary,
                  border: `1px solid ${theme.colors.primary}`,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: locating ? 'not-allowed' : 'pointer',
                }}
              >
                {locating ? '📍 Ubicando…' : coords ? '📍 Cerca mío ✓' : '📍 Usar mi ubicación'}
              </button>
              {coords && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.colors.textSecondary, fontSize: 13 }}>
                  Radio:
                  <select
                    value={radiusKm}
                    onChange={(e) => setRadiusKm(e.target.value)}
                    style={{ ...inputStyle, padding: '6px 8px', width: 'auto' }}
                  >
                    <option value="5">5 km</option>
                    <option value="10">10 km</option>
                    <option value="15">15 km</option>
                    <option value="25">25 km</option>
                    <option value="50">50 km</option>
                  </select>
                </label>
              )}
              {coords && (
                <button
                  type="button"
                  onClick={() => { setCoords(null); setZoneAlertOn(false); }}
                  style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  quitar
                </button>
              )}
            </div>
            {coords && (
              <button
                type="button"
                onClick={subscribeZoneAlert}
                disabled={zoneAlertBusy || zoneAlertOn}
                style={{
                  marginTop: 8, padding: '7px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: `1px solid ${theme.colors.primary}`,
                  background: zoneAlertOn ? alpha('primary', '20') : 'transparent',
                  color: theme.colors.primary, cursor: zoneAlertBusy || zoneAlertOn ? 'default' : 'pointer',
                }}
              >
                {zoneAlertOn ? '🔔 Te avisaremos de partidos nuevos acá' : `🔔 Avisarme de partidos nuevos (${radiusKm} km)`}
              </button>
            )}
            {geoError && (
              <div style={{ color: theme.colors.danger, fontSize: 12, marginTop: 6 }}>{geoError}</div>
            )}
          </div>

          <div>
            <label htmlFor="discover-date" style={labelStyle}>Fecha</label>
            <input
              id="discover-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="discover-from" style={labelStyle}>Desde</label>
            <input
              id="discover-from"
              type="time"
              value={timeFrom}
              onChange={(e) => setTimeFrom(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="discover-to" style={labelStyle}>Hasta</label>
            <input
              id="discover-to"
              type="time"
              value={timeTo}
              onChange={(e) => setTimeTo(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div>
            <label htmlFor="discover-gender" style={labelStyle}>Género</label>
            <select
              id="discover-gender"
              value={gender}
              onChange={(e) => setGender(e.target.value as GenderFilter)}
              style={inputStyle}
            >
              <option value="">Todos</option>
              <option value="MIXED">Mixto</option>
              <option value="MALE">Masculino</option>
              <option value="FEMALE">Femenino</option>
            </select>
          </div>

          <div>
            <label htmlFor="discover-skill" style={labelStyle}>Nivel</label>
            <select
              id="discover-skill"
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
              style={inputStyle}
            >
              <option value="">Cualquiera</option>
              <option value="1">1 — Principiante</option>
              <option value="2">2 — Básico</option>
              <option value="3">3 — Intermedio</option>
              <option value="4">4 — Avanzado</option>
              <option value="5">5 — Experto</option>
            </select>
          </div>

          <div style={{ gridColumn: isMobile ? 'auto' : 'span 2', display: 'flex', alignItems: 'end' }}>
            <button
              type="submit"
              disabled={loading || !canSearch}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: !canSearch ? theme.colors.border : theme.colors.primary,
                color: theme.colors.background,
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 700,
                cursor: !canSearch ? 'not-allowed' : 'pointer',
                fontFamily: theme.fonts.heading,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <SearchIcon size={16} />
              {loading ? 'Buscando…' : 'Buscar partidos'}
            </button>
          </div>
        </form>

        {/* Error banner */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              background: alpha('danger', '20'),
              border: `1px solid ${theme.colors.danger}`,
              borderRadius: 10,
              color: theme.colors.danger,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.colors.textSecondary }}>
            Buscando partidos…
          </div>
        ) : searched && matches.length === 0 && !error ? (
          <div
            style={{
              textAlign: 'center',
              padding: 40,
              background: theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 16,
              color: theme.colors.textSecondary,
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚽</div>
            <div style={{ fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
              No encontramos partidos
            </div>
            <div style={{ fontSize: 14 }}>Ampliá la zona o cambiá los filtros.</div>
          </div>
        ) : matches.length > 0 ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 12 }}>
              {matches.length} resultado{matches.length === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {matches.map((m) => {
                const genderLabel =
                  m.matchGenderType === 'MALE'
                    ? 'Masculino'
                    : m.matchGenderType === 'FEMALE'
                      ? 'Femenino'
                      : 'Mixto';
                const skillLabel =
                  m.skillLevelMin != null || m.skillLevelMax != null
                    ? ` · Nivel ${m.skillLevelMin ?? '?'}–${m.skillLevelMax ?? '?'}`
                    : '';
                return (
                  <div
                    key={m.id}
                    style={{
                      background: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: 14,
                      padding: 16,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                      <div
                        style={{
                          flex: 1,
                          fontWeight: 700,
                          fontSize: 16,
                          color: theme.colors.text,
                          fontFamily: theme.fonts.heading,
                        }}
                      >
                        {m.location}
                      </div>
                      {m.distanceKm != null && (
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: 8,
                            background: alpha('primary', '22'),
                            color: theme.colors.primary,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          ~{m.distanceKm} km
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginBottom: 2 }}>
                      {m.date} · {m.time} · {m.currentPlayers}/{m.maxTotalPlayers} jugadores
                    </div>
                    <div style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                      {genderLabel}{skillLabel}
                    </div>
                    <div style={{ fontSize: 13, color: theme.colors.primary, marginTop: 8, fontWeight: 700 }}>
                      Organizador · ⭐ {m.creator.rating}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/match/${m.id}/preview`)}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          background: theme.colors.background,
                          color: theme.colors.text,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: 10,
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        Ver partido
                      </button>
                      <button
                        type="button"
                        onClick={() => setApplyMatchId(m.id)}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          background: theme.colors.primary,
                          color: theme.colors.background,
                          border: 'none',
                          borderRadius: 10,
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: 'pointer',
                          fontFamily: theme.fonts.heading,
                        }}
                      >
                        Postularme
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: 40,
              background: theme.colors.surface,
              border: `1px dashed ${theme.colors.border}`,
              borderRadius: 16,
              color: theme.colors.textSecondary,
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔎</div>
            <div style={{ fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
              Ingresá una zona para empezar
            </div>
            <div style={{ fontSize: 14 }}>
              Escribí el barrio o ciudad y aplicá filtros opcionales de fecha, horario, género y nivel.
            </div>
          </div>
        )}
      </div>

      {applyMatchId && (
        <ApplyToPublicMatchModal
          isOpen={Boolean(applyMatchId)}
          matchId={applyMatchId}
          onClose={() => setApplyMatchId(null)}
          onApplied={() => {
            // success state in modal; nothing extra here
          }}
        />
      )}
    </div>
  );
};

export default DiscoverMatchesPage;
